import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarInset, SidebarProvider } from 'src/components/ui/sidebar';
import { ThemeToggle } from 'src/components/common/ThemeToggle';
import { getUser, isAuthenticated } from 'src/utils/authStorage';
import Header from '../Header/Header';
import AppSidebar from '../Sidebar/AppSidebar';

const SkipLink = () => (
  <a
    href="#main-content"
    className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[99999] focus:rounded-md focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-lg focus:outline-none focus:ring-2"
    style={{
      background: 'var(--surface)',
      color: 'var(--primary)',
      boxShadow: 'var(--shadow-lg)',
      '--tw-ring-color': 'var(--ring)',
    }}
  >
    Skip to main content
  </a>
);

const MainLayout = () => {
  const location = useLocation();
  const { pathname } = location;

  const [headerEndContent, setHeaderEndContent] = useState(null);
  const [headerBreadcrumbs, setHeaderBreadcrumbs] = useState(null);
  const outletContext = useMemo(
    () => ({ setHeaderEndContent, setHeaderBreadcrumbs }),
    [setHeaderEndContent, setHeaderBreadcrumbs]
  );

  useEffect(() => {
    setHeaderEndContent(null);
    setHeaderBreadcrumbs(null);
  }, [pathname]);

  const sidebarRoutePrefixes = [
    '/change-password',
    '/resume-preview',
    '/profile-dashboard',
    '/my-skills',
    '/my-applications',
    '/activate-account',
  ];
  const authenticated = isAuthenticated();
  const user = getUser();
  const hasAck = user?.ack === true;

  const isOpeningsRoute = pathname.startsWith('/openings');
  const isLegalRoute =
    pathname === '/terms' ||
    pathname.startsWith('/terms/') ||
    pathname === '/privacy' ||
    pathname.startsWith('/privacy/');

  const shouldShowSidebar = sidebarRoutePrefixes.some((route) =>
    pathname.startsWith(route)
  )
    ? true
    : (authenticated && isOpeningsRoute) ||
      (authenticated && hasAck && isLegalRoute);

  if (!shouldShowSidebar) {
    // Legal pages: show standalone version until onboarding ack is done
    if (isLegalRoute && !hasAck) {
      return (
        <div className="flex min-h-screen overflow-x-hidden">
          <SkipLink />
          {/* No app header on the standalone legal view — float the theme
              toggle top-right so dark/light is still reachable here. */}
          <div className="fixed right-4 top-4 z-[9999]">
            <ThemeToggle />
          </div>
          <main id="main-content" className="flex min-h-0 flex-1 flex-col">
            <Outlet context={outletContext} />
          </main>
        </div>
      );
    }

    // Auth pages handle their own navigation bar (dark AHEDGE nav)
    const isAuthRoute = ['/', '/login', '/join-us'].includes(pathname);
    if (isAuthRoute) {
      return (
        <div className="flex min-h-screen overflow-x-hidden">
          <SkipLink />
          <main id="main-content" className="flex min-h-0 flex-1 flex-col">
            <Outlet context={outletContext} />
          </main>
        </div>
      );
    }

    const isCreateLink = pathname.startsWith('/create-link');

    return (
      <div className="flex min-h-screen" style={{ overflowX: 'clip' }}>
        <SkipLink />
        <div
          className="flex min-h-0 flex-1 flex-col"
          style={{ overflowX: 'clip' }}
        >
          <div
            className={`sticky top-0 z-[9999]${
              isCreateLink ? ' create-link-header' : ''
            }`}
          >
            <Header
              useContentContainer={isCreateLink}
              contentPaddingClassName="px-4 md:px-10"
              endContent={headerEndContent}
            />
          </div>
          <main
            id="main-content"
            className="flex min-h-0 flex-1 flex-col bg-gradient-to-b from-blue-50 via-white to-blue-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950"
          >
            <Outlet context={outletContext} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ '--sidebar-width-icon': '4rem' }}>
      <SkipLink />
      <AppSidebar />
      <SidebarInset className="min-h-0 flex flex-col">
        <div
          className="sticky top-0 z-[9999] w-full bg-[var(--surface)]"
          // Must stay above ResumePreview accordions (they use sticky header z-index: 80)
          style={{ position: 'sticky', top: 0, zIndex: 9999 }}
        >
          <Header
            showSidebarTrigger
            showBranding={false}
            endContent={headerEndContent}
            breadcrumbOverride={headerBreadcrumbs}
          />
        </div>
        {/* overflow-x: clip (not hidden) — 'hidden' creates a scroll container
            which silently breaks position:sticky on all descendants.
            'clip' visually clips horizontal overflow without becoming a scroll container. */}
        <main
          id="main-content"
          className="flex min-h-0 flex-1 flex-col bg-ah-surface-page"
          style={{ overflowX: 'clip' }}
        >
          <Outlet context={outletContext} />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default MainLayout;
